// Generated from b2bservices/src/common/productRequirements/clpPhrases.ts.
// Do not edit. Run yarn requirements:sync in a paired checkout.

export interface IChemicalPhraseFill {
  code: string;
  slotIndex: number;
  value?: string;
}

// Slots are persisted by index (IChemicalPhraseFill.slotIndex). Append new slots
// at the end of a code's `slots` array — never reorder existing ones, or stored
// fills will rebind to the wrong slot.

export interface IPhraseSlot {
  hint: string;
  placeholder?: string;
  optional: boolean;
  emptyFallback?: string;
}

export interface IPhraseSpec {
  template: string;
  slots: IPhraseSlot[];
}

const exposureRouteSlot: IPhraseSlot = {
  hint: 'Exponeringsväg, om definitivt bevisad',
  placeholder: 't.ex. "vid inandning"',
  optional: true,
};

const cmrEffectSlot: IPhraseSlot = {
  hint: 'Specifik effekt, om känd',
  placeholder: 't.ex. "skadar fertiliteten"',
  optional: true,
};

const organSlot: IPhraseSlot = {
  hint: 'Vilka organ som påverkas, om känt',
  placeholder: 't.ex. "i levern"',
  optional: true,
};

export const HAZARD_STATEMENT_PLACEHOLDERS: Record<string, IPhraseSpec> = {
  H340: {
    template: 'Kan orsaka genetiska defekter {0}.',
    slots: [exposureRouteSlot],
  },
  H341: {
    template: 'Misstänks kunna orsaka genetiska defekter {0}.',
    slots: [exposureRouteSlot],
  },
  H350: {
    template: 'Kan orsaka cancer {0}.',
    slots: [exposureRouteSlot],
  },
  H351: {
    template: 'Misstänks kunna orsaka cancer {0}.',
    slots: [exposureRouteSlot],
  },
  H360: {
    template: 'Kan skada fertiliteten eller det ofödda barnet {0} {1}.',
    slots: [cmrEffectSlot, exposureRouteSlot],
  },
  H361: {
    template: 'Misstänks kunna skada fertiliteten eller det ofödda barnet {0} {1}.',
    slots: [cmrEffectSlot, exposureRouteSlot],
  },
  H370: {
    template: 'Orsakar organskador {0} {1}.',
    slots: [organSlot, exposureRouteSlot],
  },
  H371: {
    template: 'Kan orsaka organskador {0} {1}.',
    slots: [organSlot, exposureRouteSlot],
  },
  H372: {
    template: 'Orsakar organskador {0} genom lång eller upprepad exponering {1}.',
    slots: [organSlot, exposureRouteSlot],
  },
  H373: {
    template: 'Kan orsaka organskador {0} genom lång eller upprepad exponering {1}.',
    slots: [organSlot, exposureRouteSlot],
  },
  EUH208: {
    template: 'Innehåller {0}. Kan orsaka en allergisk reaktion',
    slots: [
      {
        hint: 'Allergiframkallande ämne',
        placeholder: 't.ex. "natriumdivinylsulfid"',
        optional: false,
      },
    ],
  },
};

const contactAdditionSlot: IPhraseSlot = {
  hint: 'Tillägg (valfritt)',
  placeholder: 't.ex. "/akutmottagning"',
  optional: true,
};

export const SECURITY_NOTICE_PLACEHOLDERS: Record<string, IPhraseSpec> = {
  P230: {
    template: 'Ska hållas fuktigt med {0}.',
    slots: [{ hint: 'Fuktighetsmedel', placeholder: 't.ex. "vatten"', optional: false }],
  },
  P231: {
    template: 'Hantera och förvara innehållet under inert gas{0}.',
    slots: [{ hint: 'Alternativ atmosfär (valfritt)', placeholder: 't.ex. "/kvävgas"', optional: true }],
  },
  P241: {
    template: 'Använd explosionssäker {0} utrustning.',
    slots: [
      {
        hint: 'Typ av utrustning',
        placeholder: 't.ex. "elektrisk, ventilations-, belysnings-"',
        optional: false,
      },
    ],
  },
  P250: {
    template: 'Får inte utsättas för malning/stötar/friktion{0}.',
    slots: [
      {
        hint: 'Ytterligare förhållanden (valfritt)',
        placeholder: 't.ex. "/värme"',
        optional: true,
      },
    ],
  },
  P264: {
    template: 'Tvätta {0} grundligt efter användning.',
    slots: [{ hint: 'Vad ska tvättas', placeholder: 't.ex. "händer"', optional: false }],
  },
  P284: {
    template: 'Använd andningsskydd {0}.',
    slots: [
      {
        hint: 'Förbehåll (valfritt)',
        placeholder: 't.ex. "vid otillräcklig ventilation"',
        optional: true,
      },
    ],
  },
  P312: {
    template: 'Vid obehag, kontakta GIFTINFORMATIONSCENTRALEN/läkare{0}.',
    slots: [contactAdditionSlot],
  },
  P231_P232: {
    template: 'Hantera och förvara innehållet under inert gas{0}. Skyddas från fukt.',
    slots: [{ hint: 'Alternativ atmosfär (valfritt)', placeholder: 't.ex. "/kvävgas"', optional: true }],
  },
  P301_P310: {
    template: 'VID FÖRTÄRING: Kontakta genast GIFTINFORMATIONSCENTRALEN/läkare{0}.',
    slots: [contactAdditionSlot],
  },
  P301_P312: {
    template: 'VID FÖRTÄRING: Vid obehag, kontakta GIFTINFORMATIONSCENTRALEN/läkare{0}.',
    slots: [contactAdditionSlot],
  },
  P302_P352: {
    template: 'VID HUDKONTAKT: Tvätta med mycket vatten{0}.',
    slots: [{ hint: 'Tvättmedel/tillsats (valfritt)', placeholder: 't.ex. "/tvål"', optional: true }],
  },
  P308_P311: {
    template:
      'VID exponering eller misstanke om exponering: Kontakta GIFTINFORMATIONSCENTRALEN/läkare{0}.',
    slots: [contactAdditionSlot],
  },
  P342_P311: {
    template: 'Vid besvär i luftvägarna: Kontakta GIFTINFORMATIONSCENTRALEN/läkare{0}.',
    slots: [contactAdditionSlot],
  },
  P370_P378: {
    template: 'Vid brand: Släck med {0}.',
    slots: [{ hint: 'Släckmedel', placeholder: 't.ex. "vatten"', optional: false }],
  },
  P370_P380_P375_P378: {
    template:
      'Vid brand: Utrym området. Bekämpa branden på avstånd på grund av explosionsrisken. {0}',
    slots: [
      {
        hint: 'Släckanvisning (valfritt)',
        placeholder: 't.ex. "Släck med vatten."',
        optional: true,
      },
    ],
  },
  P302_P335_P334: {
    template:
      'VID HUDKONTAKT: Borsta bort lösa partiklar från huden. Skölj under kallt vatten {0}.',
    slots: [
      {
        hint: 'Alternativ behandling (valfritt)',
        placeholder: 't.ex. "eller använd våta omslag"',
        optional: true,
      },
    ],
  },
  P303_P361_P353: {
    template:
      'VID HUDKONTAKT (även håret): Ta omedelbart av alla nedstänkta kläder. Skölj huden med vatten {0}.',
    slots: [
      {
        hint: 'Alternativ (valfritt)',
        placeholder: 't.ex. "eller duscha"',
        optional: true,
      },
    ],
  },
  P406: {
    template: 'Förvaras i korrosionsbeständig{0} behållare med beständigt innerhölje.',
    slots: [
      {
        hint: 'Alternativt material (valfritt)',
        placeholder: 't.ex. "/glasförstärkt"',
        optional: true,
      },
    ],
  },
  P411: {
    template: 'Förvaras vid högst {0}°C{1}.',
    slots: [
      { hint: 'Maxtemperatur (°C)', placeholder: 't.ex. "25"', optional: false },
      { hint: 'Maxtemperatur i °F (valfritt)', placeholder: 't.ex. "/77°F"', optional: true },
    ],
  },
  P413: {
    template: 'Bulkprodukter som väger mer än {0} kg{1} förvaras vid högst {2}°C{3}.',
    slots: [
      { hint: 'Vikt (kg)', placeholder: 't.ex. "100"', optional: false },
      { hint: 'Vikt i lbs (valfritt)', placeholder: 't.ex. "/220 lbs"', optional: true },
      { hint: 'Maxtemperatur (°C)', placeholder: 't.ex. "25"', optional: false },
      { hint: 'Maxtemperatur i °F (valfritt)', placeholder: 't.ex. "/77°F"', optional: true },
    ],
  },
  P501: {
    template: 'Innehållet/behållaren lämnas till {0}.',
    slots: [
      {
        hint: 'Hanteringsanvisning',
        placeholder: 't.ex. "godkänd avfallsanläggning"',
        optional: false,
      },
    ],
  },
  P310: {
    template: 'Kontakta genast {0}.',
    slots: [
      {
        hint: 'Kontakt för medicinsk rådgivning (valfritt)',
        placeholder: 't.ex. läkare',
        optional: true,
        emptyFallback: 'GIFTINFORMATIONSCENTRALEN/läkare',
      },
    ],
  },
  P311: {
    template: 'Kontakta {0}.',
    slots: [
      {
        hint: 'Kontakt för medicinsk rådgivning (valfritt)',
        placeholder: 't.ex. läkare',
        optional: true,
        emptyFallback: 'GIFTINFORMATIONSCENTRALEN/läkare',
      },
    ],
  },
  P320: {
    template: 'Särskild behandling krävs omedelbart (se {0} på etiketten).',
    slots: [
      {
        hint: 'Hänvisning till första hjälpen-anvisningar',
        placeholder: 't.ex. första hjälpen-anvisningarna',
        optional: false,
      },
    ],
  },
  P321: {
    template: 'Särskild behandling (se {0} på etiketten).',
    slots: [
      {
        hint: 'Hänvisning till första hjälpen-anvisningar',
        placeholder: 't.ex. första hjälpen-anvisningarna',
        optional: false,
      },
    ],
  },
  P401: {
    template: 'Förvaras i enlighet med {0}.',
    slots: [{ hint: 'Förvaringsbestämmelser', placeholder: 't.ex. nationella bestämmelser', optional: false }],
  },
  P503: {
    template: 'Rådfråga {0}.',
    slots: [
      {
        hint: 'Kontakt och avfallsråd (valfritt)',
        placeholder: 't.ex. leverantören om återvinning',
        optional: true,
        emptyFallback: 'tillverkare/leverantör om bortskaffande/återvinning/återanvändning',
      },
    ],
  },
};

export const getPhraseSpec = (code: string): IPhraseSpec | undefined =>
  HAZARD_STATEMENT_PLACEHOLDERS[code] ?? SECURITY_NOTICE_PLACEHOLDERS[code];

export const getFillValue = (
  fills: readonly IChemicalPhraseFill[] | undefined,
  code: string,
  slotIndex: number,
): string => fills?.find(f => f.code === code && f.slotIndex === slotIndex)?.value ?? '';

export const resolvePhrase = (spec: IPhraseSpec, slotValues: string[]): string => {
  let out = spec.template;
  spec.slots.forEach((slot, i) => {
    const rawValue = slotValues[i] ?? '';
    const value = rawValue.trim();
    if (value) {
      const separator = /^\s/.test(rawValue) ? ' ' : '';
      out = out.replace(`{${i}}`, `${separator}${value}`);
    } else if (slot.emptyFallback) {
      out = out.replace(`{${i}}`, slot.emptyFallback);
    } else {
      out = out.replace(new RegExp(`\\s*\\{${i}\\}\\s*`), ' ');
    }
  });
  return out
    .replace(/\s+/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .trim();
};

export const upsertFill = (
  fills: readonly IChemicalPhraseFill[] | undefined,
  code: string,
  slotIndex: number,
  value: string,
): IChemicalPhraseFill[] => {
  const next = [...(fills ?? [])];
  const idx = next.findIndex(f => f.code === code && f.slotIndex === slotIndex);
  if (idx >= 0) {
    next[idx] = { ...next[idx], value };
  } else {
    next.push({ code, slotIndex, value });
  }
  return next;
};

/** Only selected phrases with required slots contribute a completion gap. */
export const phraseFillsComplete = (
  codes: readonly string[] | undefined,
  fills: readonly IChemicalPhraseFill[] | undefined,
): boolean => (codes || []).every(code => {
  const spec = getPhraseSpec(code);
  return !spec || spec.slots.every((slot, index) => {
    const value = getFillValue(fills, code, index);
    return slot.optional || (typeof value === 'string' && !!value.trim());
  });
});

export const HAZARD_STATEMENT_GROUPS = [
  { label: '1.1 Fargoangivelser för fysikaliska faror', values: ['H200', 'H201', 'H202', 'H203', 'H204', 'H205', 'H206', 'H207', 'H208', 'H220', 'H221', 'H222', 'H223', 'H224', 'H225', 'H226', 'H228', 'H229', 'H230', 'H231', 'H232', 'H240', 'H241', 'H242', 'H250', 'H251', 'H252', 'H260', 'H261', 'H270', 'H271', 'H272', 'H280', 'H281', 'H290'] },
  { label: '1.2 Faroangivelser för hälsofaror', values: ['H300', 'H301', 'H302', 'H304', 'H310', 'H311', 'H312', 'H314', 'H315', 'H317', 'H318', 'H319', 'H330', 'H331', 'H332', 'H334', 'H335', 'H336', 'H340', 'H341', 'H350', 'H350i', 'H351', 'H360', 'H360D', 'H360Df', 'H360F', 'H360FD', 'H360Fd', 'H361', 'H361d', 'H361f', 'H361fd', 'H362', 'H370', 'H371', 'H372', 'H373', 'EUH380', 'EUH381', 'H300_H310', 'H300_H310_H330', 'H300_H330', 'H310_H330', 'H310_H310_H330', 'H301_H311', 'H301_H331', 'H311_H331', 'H301_H311_H331', 'H302_H312', 'H302_H332', 'H312_H332', 'H302_H312_H332'] },
  { label: '1.3 Faroangivelser för miljöfaror', values: ['H400', 'H410', 'H411', 'H412', 'H413', 'H420', 'EUH430', 'EUH431', 'EUH440', 'EUH441', 'EUH450', 'EUH451'] },
  { label: 'Del 2. Kompletterande faroinformation', values: ['EUH014', 'EUH018', 'EUH019', 'EUH029', 'EUH031', 'EUH032', 'EUH044', 'EUH059', 'EUH066', 'EUH070', 'EUH071'] },
  { label: 'Del 3. Kompletterande märkning/information om vissa ämnen eller blandningar', values: ['EUH201', 'EUH201A', 'EUH202', 'EUH203', 'EUH204', 'EUH205', 'EUH206', 'EUH207', 'EUH208', 'EUH209', 'EUH209A', 'EUH210', 'EUH401'] },
] as const;

export const SECURITY_NOTICE_GROUPS = [
  { label: 'Skyddsangivelser – Allmänt', values: ['P101', 'P102', 'P103'] },
  { label: 'Skyddsangivelser – Förebyggande', values: ['P201', 'P202', 'P210', 'P211', 'P212', 'P220', 'P222', 'P223', 'P230', 'P231', 'P232', 'P233', 'P234', 'P235', 'P240', 'P241', 'P242', 'P243', 'P244', 'P250', 'P251', 'P260', 'P261', 'P262', 'P263', 'P264', 'P270', 'P271', 'P272', 'P273', 'P280', 'P282', 'P283', 'P284', 'P231_P232', 'P310', 'P311', 'P312', 'P314', 'P315', 'P320', 'P321', 'P330', 'P331', 'P363', 'P377', 'P381', 'P390', 'P391', 'P301_P310', 'P301_P312', 'P302_P334', 'P302_P352', 'P304_P340', 'P306_P360', 'P308_P311', 'P308_P313', 'P332_P313', 'P333_P313', 'P336_P315', 'P337_P313', 'P342_P311', 'P361_P364', 'P362_P364', 'P370_P376', 'P370_P378', 'P301_P330_P331', 'P302_P335_P334', 'P303_P361_P353', 'P305_P351_P338', 'P370_P380_P375', 'P371_P380_P375', 'P370_P372_P380_P373', 'P370_P380_P375_P378'] },
  { label: 'Skyddsangivelser – Förvaring', values: ['P401', 'P402', 'P403', 'P404', 'P405', 'P406', 'P407', 'P410', 'P411', 'P412', 'P413', 'P420', 'P402_P404', 'P403_P233', 'P403_P235', 'P410_P403', 'P410_P412'] },
  { label: '  Skyddsangivelser – Avfall', values: ['P501', 'P503', 'P502'] },
] as const;
